import { Raycaster, Vector2, Quaternion, Vector3, type Group, type Mesh, type PerspectiveCamera } from 'three';
import { physics, accelerometer as accelConfig } from './MagicBallConfig';

const REST_QUAT = new Quaternion();

export class BallInteraction {
  private ballShellGroup: Group;
  private ballMesh: Mesh;
  private camera: PerspectiveCamera;
  private canvas: HTMLCanvasElement;
  private orbitControls: { enabled: boolean };
  private raycaster = new Raycaster();
  private mouse = new Vector2();

  private isDragging = false;
  private isReturning = false;
  private prevX = 0;
  private prevY = 0;
  private maxTilt = 0;
  private onShake: (() => void) | null = null;
  private deviceTiltX = 0;
  private deviceTiltY = 0;

  private isShaking = false;
  private shakeTime = 0;
  private shakeDuration = 0.6;
  private shakeAxis1 = new Vector3();
  private shakeAxis2 = new Vector3();

  private onDownBound: (e: MouseEvent | TouchEvent) => void;
  private onMoveBound: (e: MouseEvent | TouchEvent) => void;
  private onUpBound: () => void;

  constructor(
    ballShellGroup: Group,
    ballMesh: Mesh,
    camera: PerspectiveCamera,
    canvas: HTMLCanvasElement,
    orbitControls: { enabled: boolean },
  ) {
    this.ballShellGroup = ballShellGroup;
    this.ballMesh = ballMesh;
    this.camera = camera;
    this.canvas = canvas;
    this.orbitControls = orbitControls;

    this.onDownBound = (e) => this.onDown(e);
    this.onMoveBound = (e) => this.onMove(e);
    this.onUpBound = () => this.onUp();

    canvas.addEventListener('mousedown', this.onDownBound);
    canvas.addEventListener('touchstart', this.onDownBound, { passive: false });
    window.addEventListener('mousemove', this.onMoveBound);
    window.addEventListener('touchmove', this.onMoveBound, { passive: false });
    window.addEventListener('mouseup', this.onUpBound);
    window.addEventListener('touchend', this.onUpBound);
  }

  private enabled = true;

  get onShakeTriggered(): (() => void) | null {
    return this.onShake;
  }

  set onShakeTriggered(cb: (() => void) | null) {
    this.onShake = cb;
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value && this.isDragging) {
      this.isDragging = false;
      this.isReturning = true;
      this.orbitControls.enabled = true;
    }
  }

  simulateShake(): void {
    if (this.isShaking) return;
    this.isShaking = true;
    this.shakeTime = 0;
    this.shakeAxis1.set(Math.random() - 0.5, 0, Math.random() - 0.5).normalize();
    this.shakeAxis2.set(Math.random() - 0.5, 0, Math.random() - 0.5).normalize();
  }

  applyDeviceTilt(tiltX: number, tiltY: number): void {
    this.deviceTiltX = tiltX;
    this.deviceTiltY = tiltY;
  }

  update(delta: number): void {
    if (this.isShaking) {
      this.shakeTime += delta;
      const t = this.shakeTime / this.shakeDuration;

      if (t >= 1) {
        this.isShaking = false;
        this.isReturning = true;
        this.onShake?.();
      } else {
        const decay = 1 - t;
        const intensity = 0.5 * decay;
        const freq1 = Math.sin(t * Math.PI * 8);
        const freq2 = Math.cos(t * Math.PI * 6);

        const q1 = new Quaternion().setFromAxisAngle(this.shakeAxis1, freq1 * intensity);
        const q2 = new Quaternion().setFromAxisAngle(this.shakeAxis2, freq2 * intensity * 0.7);
        this.ballShellGroup.quaternion.copy(q1.multiply(q2));
      }
      return;
    }

    if (!this.isDragging && (Math.abs(this.deviceTiltX) > 0.02 || Math.abs(this.deviceTiltY) > 0.02)) {
      const targetQx = new Quaternion().setFromAxisAngle(
        new Vector3(0, 0, 1), -this.deviceTiltX * accelConfig.tiltSensitivity,
      );
      const targetQy = new Quaternion().setFromAxisAngle(
        new Vector3(1, 0, 0), this.deviceTiltY * accelConfig.tiltSensitivity,
      );
      const target = targetQy.multiply(targetQx);
      const t = Math.min(1, accelConfig.tiltSmoothing * delta);
      this.ballShellGroup.quaternion.slerp(target, t);
      this.isReturning = false;
    } else if (this.isReturning && !this.isDragging) {
      const t = Math.min(1, physics.ballReturnSpeed * delta);
      this.ballShellGroup.quaternion.slerp(REST_QUAT, t);

      if (this.ballShellGroup.quaternion.dot(REST_QUAT) > 0.999) {
        this.ballShellGroup.quaternion.identity();
        this.isReturning = false;
      }
    }
  }

  private clientPos(e: MouseEvent | TouchEvent): { x: number; y: number } | null {
    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
  }

  private hitTest(clientX: number, clientY: number): boolean {
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObject(this.ballMesh, false).length > 0;
  }

  private onDown(e: MouseEvent | TouchEvent): void {
    if (!this.enabled) return;
    const pos = this.clientPos(e);
    if (!pos) return;
    if ((e.target as HTMLElement) !== this.canvas) return;

    if (this.hitTest(pos.x, pos.y)) {
      e.preventDefault();
      this.isDragging = true;
      this.isReturning = false;
      this.maxTilt = 0;
      this.prevX = pos.x;
      this.prevY = pos.y;
      this.orbitControls.enabled = false;
    }
  }

  private onMove(e: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;
    const pos = this.clientPos(e);
    if (!pos) return;

    const dx = pos.x - this.prevX;
    const dy = pos.y - this.prevY;
    this.prevX = pos.x;
    this.prevY = pos.y;

    const camRight = new Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const camUp = new Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);

    const qx = new Quaternion().setFromAxisAngle(camRight, dy * physics.dragSpeed);
    const qy = new Quaternion().setFromAxisAngle(camUp, dx * physics.dragSpeed);

    this.ballShellGroup.quaternion.premultiply(qy).premultiply(qx);

    const localUp = new Vector3(0, 1, 0).applyQuaternion(this.ballShellGroup.quaternion);
    const tilt = Math.acos(Math.min(1, localUp.dot(new Vector3(0, 1, 0))));
    if (tilt > this.maxTilt) this.maxTilt = tilt;
  }

  private onUp(): void {
    if (this.isDragging) {
      const shakeThreshold = 0.4;
      const shouldShake = this.maxTilt > shakeThreshold;
      this.isDragging = false;
      this.isReturning = true;
      this.orbitControls.enabled = true;
      if (shouldShake && this.onShake) this.onShake();
    }
  }

  dispose(): void {
    this.canvas.removeEventListener('mousedown', this.onDownBound);
    this.canvas.removeEventListener('touchstart', this.onDownBound);
    window.removeEventListener('mousemove', this.onMoveBound);
    window.removeEventListener('touchmove', this.onMoveBound);
    window.removeEventListener('mouseup', this.onUpBound);
    window.removeEventListener('touchend', this.onUpBound);
  }
}
